/* eslint-disable */
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import '../LoginPage/login.scss'
import darpanIcon from '../../Assets/images/svg/darpan_icon.svg'
import {
  WELCOME_TO_DARPAN,
  DARPAN_TOOL_TAGLINE,
} from '../../Constants/strings'
import lottie from 'lottie-web'
import PersonIcon from '@mui/icons-material/Person'
import LockIcon from '@mui/icons-material/Lock'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import EmailIcon from '@mui/icons-material/Email'
import PhoneIcon from '@mui/icons-material/Phone'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

export function RegisterPage({ onSwitchToLogin, onRegisterSuccess, onBackToHome }) {
  const [animationData, setAnimationData] = useState(null)
  const animationContainer = useRef(null)
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    userType: 'buyer'
  })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    import('../../Assets/images/json/dar_man_monitoring_dashboard.json').then(
      ({ default: animationData }) => {
        setAnimationData(animationData)
      },
    )
  }, [])

  useEffect(() => {
    if (animationData) {
      lottie.loadAnimation({
        container: animationContainer.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData,
      })
    }
  }, [animationData])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required'
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required'
    }
    
    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required'
    } else if (!/^\+?[\d\s\-\(\)]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number'
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }
    
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    setErrors({})

    try {
      const { confirmPassword, ...registrationData } = formData
      const result = await onRegisterSuccess?.(registrationData)

      if (result && result.success) {
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          password: '',
          confirmPassword: '',
          userType: 'buyer'
        })
      }
    } catch (error) {
      console.error('Registration error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const renderWelcomText = () => {
    return (
      <div className="welcome-display-on-mobile">
        <img className="darpan-icon" src={darpanIcon} alt="Darpan Icon" />
        <div className="welcome-text-mobile">{WELCOME_TO_DARPAN}</div>
        <div className="tool-tagline-mobile">{DARPAN_TOOL_TAGLINE}</div>
      </div>
    )
  }

  const renderRegisterForm = () => {
    return (
      <div className="login-form-container" style={{ maxWidth: '600px' }}>
        {onSwitchToLogin && (
          <button
            type="button"
            className="back-button"
            onClick={onSwitchToLogin}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              marginBottom: '1.5rem',
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              padding: '0.5rem',
              fontSize: '14px'
            }}
          >
            <ArrowBackIcon style={{ fontSize: '18px' }} />
            Back to Login
          </button>
        )}
        <form onSubmit={handleSubmit} className="login-form" noValidate>
          {errors.form && (
            <div className="error-message">
              {errors.form}
            </div>
          )}

          <div className="register-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.2rem' }}>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="firstName">First Name</label>
              <div className="input-wrapper">
                <PersonIcon className="input-icon" />
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                  disabled={isLoading}
                  autoComplete="given-name"
                />
              </div>
              {errors.firstName && <span className="field-error">{errors.firstName}</span>}
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="lastName">Last Name</label>
              <div className="input-wrapper">
                <PersonIcon className="input-icon" />
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                  disabled={isLoading}
                  autoComplete="family-name"
                />
              </div>
              {errors.lastName && <span className="field-error">{errors.lastName}</span>}
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <EmailIcon className="input-icon" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  disabled={isLoading}
                  autoComplete="email"
                />
              </div>
              {errors.email && <span className="field-error">{errors.email}</span>}
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="phone">Phone Number</label>
              <div className="input-wrapper">
                <PhoneIcon className="input-icon" />
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter your phone number"
                  disabled={isLoading}
                  autoComplete="tel"
                />
              </div>
              {errors.phone && <span className="field-error">{errors.phone}</span>}
            </div>

            <div className="form-group mobile-full-row" style={{ marginBottom: '1rem' }}>
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <LockIcon className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </button>
              </div>
              {errors.password && <span className="field-error">{errors.password}</span>}
            </div>

            <div className="form-group mobile-full-row" style={{ marginBottom: '1rem' }}>
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="input-wrapper">
                <LockIcon className="input-icon" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm your password"
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={isLoading}
                  aria-label="Toggle password visibility"
                >
                  {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </button>
              </div>
              {errors.confirmPassword && <span className="field-error">{errors.confirmPassword}</span>}
            </div>
          </div>

          <div style={{ marginTop: '2rem' }}>
            <button
              type="submit"
              className="login-button"
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </button>
          </div>

          {onSwitchToLogin && (
            <div className="signup-link">
              Already have an account?{' '}
              <button
                type="button"
                className="link-button"
                onClick={onSwitchToLogin}
              >
                Sign in here
              </button>
            </div>
          )}
        </form>
      </div>
    )
  }

  return (
    <div className="container">
      <div className="left-panel">
        {renderWelcomText()}
        <div ref={animationContainer} className="left-panel-image"></div>
        <div className="login-display-on-mobile">
          {renderRegisterForm()}
        </div>
      </div>
      <div className="right-panel">
        <div className="content-area">
          {renderWelcomText()}
          {renderRegisterForm()}
        </div>
      </div>
    </div>
  )
}